import ReconnectingWebSocket from 'reconnecting-websocket';

import { metricsProxyRestOptions } from 'shared/cex-sdk';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';

const BULLISH_REST_BASE = 'https://api.exchange.bullish.com/trading-api';
const BULLISH_WS_BASE = 'wss://api.exchange.bullish.com';
const BULLISH_MAX_CANDLES_PER_REQUEST = 100;
const BULLISH_MAX_HISTORY = 300;
const BULLISH_MAX_TRADES = 100;
const BULLISH_HEARTBEAT_INTERVAL_MS = 240_000;

export const bullishRestClient = metricsProxyRestOptions.client!;

type BullishNumber = number | string;
type BullishFlatLevel = BullishNumber[];

interface BullishMarket {
  symbol: string;
  marketEnabled: boolean;
  spotTradingEnabled: boolean;
  tickSize: BullishNumber;
  quantityPrecision: BullishNumber;
}

interface BullishTicker {
  symbol: string;
  last: BullishNumber;
  open: BullishNumber;
  high: BullishNumber;
  low: BullishNumber;
  percentage: BullishNumber;
  baseVolume: BullishNumber;
  quoteVolume: BullishNumber;
}

interface BullishPriceLevel {
  price: BullishNumber;
  priceLevelQuantity: BullishNumber;
  type?: string;
}

export interface BullishOrderbookSnapshot {
  symbol?: string;
  sequenceNumber: BullishNumber;
  bids: BullishPriceLevel[];
  asks: BullishPriceLevel[];
}

export interface BullishCandle {
  createdAtTimestamp: BullishNumber;
  open: BullishNumber;
  high: BullishNumber;
  low: BullishNumber;
  close: BullishNumber;
  volume: BullishNumber;
}

interface BullishTrade {
  tradeId: string;
  symbol: string;
  price: BullishNumber;
  quantity: BullishNumber;
  side: string;
  isTaker: boolean;
  createdAtTimestamp: BullishNumber;
}

interface BullishWsData {
  symbol?: string;
  sequenceNumber?: BullishNumber;
  sequenceNumberRange?: BullishNumber[];
  bids?: BullishFlatLevel;
  asks?: BullishFlatLevel;
  trades?: BullishTrade[];
  last?: BullishNumber;
  open?: BullishNumber;
  high?: BullishNumber;
  low?: BullishNumber;
  percentage?: BullishNumber;
  baseVolume?: BullishNumber;
  quoteVolume?: BullishNumber;
}

interface BullishWsMessage {
  type?: string;
  dataType?: string;
  data?: BullishWsData;
  result?: {
    responseCode?: BullishNumber;
    responseCodeName?: string;
    message?: string;
  };
  error?: {
    code?: BullishNumber;
    errorCode?: BullishNumber;
    errorCodeName?: string;
  };
}

export interface BullishOrderbookState {
  bids: Map<string, string>;
  asks: Map<string, string>;
  sequence: number;
}

type BullishTopic = 'l2Orderbook' | 'anonymousTrades' | 'tick';

const BULLISH_TIMEFRAMES: Partial<Record<IChartTimeframe, string>> = {
  '1m': '1m',
  '5m': '5m',
  '30m': '30m',
  '1h': '1h',
  '6h': '6h',
  '12h': '12h',
  '24h': '1d',
  '1d': '1d',
};

const BULLISH_INTERVAL_MILLISECONDS: Record<string, number> = {
  '1m': 60_000,
  '5m': 300_000,
  '30m': 1_800_000,
  '1h': 3_600_000,
  '6h': 21_600_000,
  '12h': 43_200_000,
  '1d': 86_400_000,
};

const BULLISH_STREAMS: Array<{ path: string; topic: BullishTopic }> = [
  {
    path: '/trading-api/v1/market-data/orderbook',
    topic: 'l2Orderbook',
  },
  {
    path: '/trading-api/v1/market-data/trades',
    topic: 'anonymousTrades',
  },
  {
    path: '/trading-api/v1/market-data/tick',
    topic: 'tick',
  },
];

const isFiniteNumber = (value: unknown) =>
  value !== null &&
  value !== undefined &&
  (typeof value !== 'string' || value.trim() !== '') &&
  Number.isFinite(Number(value));

const asString = (value: BullishNumber) => String(value);

const parseSequence = (value: BullishNumber | undefined, resource: string): number => {
  const sequence = Number(value);
  if (!Number.isSafeInteger(sequence) || sequence <= 0) {
    throw new Error(`Bullish ${resource} sequence is invalid: ${String(value)}`);
  }
  return sequence;
};

const parseTimestamp = (value: BullishNumber, resource: string): number => {
  const timestamp = Number(value);
  if (!Number.isSafeInteger(timestamp) || timestamp <= 0) {
    throw new Error(`Bullish ${resource} timestamp is invalid: ${String(value)}`);
  }
  return timestamp;
};

const normalizeRestLevels = (levels: BullishPriceLevel[] | undefined): string[][] =>
  (levels ?? [])
    .filter(
      level =>
        isFiniteNumber(level?.price) &&
        isFiniteNumber(level?.priceLevelQuantity) &&
        Number(level.priceLevelQuantity) > 0,
    )
    .map(level => [asString(level.price), asString(level.priceLevelQuantity)]);

const normalizeFlatLevels = (values: BullishFlatLevel | undefined): string[][] => {
  if (!values) return [];
  if (values.length % 2 !== 0) {
    throw new Error(`Bullish orderbook contains ${values.length} flattened values`);
  }

  const levels: string[][] = [];
  for (let index = 0; index < values.length; index += 2) {
    const price = values[index];
    const size = values[index + 1];
    if (!isFiniteNumber(price) || !isFiniteNumber(size) || Number(size) < 0) {
      throw new Error(`Bullish orderbook level ${index / 2} is invalid`);
    }
    levels.push([asString(price), asString(size)]);
  }
  return levels;
};

const writeLevels = (side: Map<string, string>, levels: string[][]) => {
  levels.forEach(([price, size]) => {
    if (Number(size) === 0) side.delete(price);
    else side.set(price, size);
  });
};

const sequenceRange = (data: {
  sequenceNumber?: BullishNumber;
  sequenceNumberRange?: BullishNumber[];
}): [number, number] => {
  if (data.sequenceNumberRange?.length) {
    const first = parseSequence(data.sequenceNumberRange[0], 'orderbook first');
    const last = parseSequence(
      data.sequenceNumberRange[data.sequenceNumberRange.length - 1],
      'orderbook last',
    );
    if (last < first)
      throw new Error(`Bullish orderbook sequence range ${first}-${last} is invalid`);
    return [first, last];
  }
  const sequence = parseSequence(data.sequenceNumber, 'orderbook');
  return [sequence, sequence];
};

const applyBullishFlatSnapshot = (state: BullishOrderbookState, data: BullishWsData) => {
  const bids = normalizeFlatLevels(data.bids);
  const asks = normalizeFlatLevels(data.asks);
  if (bids.length === 0 || asks.length === 0) {
    throw new Error('Bullish WebSocket orderbook snapshot is empty');
  }
  const [, last] = sequenceRange(data);

  state.bids.clear();
  state.asks.clear();
  writeLevels(state.bids, bids);
  writeLevels(state.asks, asks);
  state.sequence = last;
};

const calculateChange = (last: BullishNumber, open: BullishNumber): string => {
  const lastNumber = Number(last);
  const openNumber = Number(open);
  if (!Number.isFinite(lastNumber) || !Number.isFinite(openNumber) || openNumber === 0) return '0';
  return normalizeNumber(((lastNumber - openNumber) / openNumber) * 100);
};

export const buildBullishMarketSymbol = (base: string, quote: string) =>
  `${base.toUpperCase()}${quote.toUpperCase()}`;

export const bullishPrecisionFromTick = (tick: BullishNumber | undefined): number => {
  if (!isFiniteNumber(tick) || Number(tick) <= 0) return 1;
  const normalized = String(tick).toLowerCase();
  if (normalized.includes('e-')) {
    const exponent = Number(normalized.split('e-')[1]);
    return Number.isFinite(exponent) ? exponent : 1;
  }
  const [, decimals = ''] = normalized.split('.');
  return decimals.replace(/0+$/, '').length;
};

export const normalizeBullishCandles = (candles: BullishCandle[]): KLine[] =>
  candles
    .filter(
      candle =>
        isFiniteNumber(candle.createdAtTimestamp) &&
        isFiniteNumber(candle.open) &&
        isFiniteNumber(candle.high) &&
        isFiniteNumber(candle.low) &&
        isFiniteNumber(candle.close) &&
        isFiniteNumber(candle.volume),
    )
    .map(
      candle =>
        [
          Number(candle.createdAtTimestamp),
          asString(candle.open),
          asString(candle.high),
          asString(candle.low),
          asString(candle.close),
          asString(candle.volume),
        ] as KLine,
    )
    .sort((left, right) => left[0] - right[0]);

export const createBullishOrderbookState = (): BullishOrderbookState => ({
  bids: new Map(),
  asks: new Map(),
  sequence: 0,
});

export const applyBullishOrderbookSnapshot = (
  state: BullishOrderbookState,
  snapshot: BullishOrderbookSnapshot,
) => {
  const bids = normalizeRestLevels(snapshot.bids);
  const asks = normalizeRestLevels(snapshot.asks);
  if (bids.length === 0 || asks.length === 0) {
    throw new Error('Bullish REST orderbook snapshot is empty');
  }

  state.bids.clear();
  state.asks.clear();
  writeLevels(state.bids, bids);
  writeLevels(state.asks, asks);
  state.sequence = parseSequence(snapshot.sequenceNumber, 'REST orderbook');
};

export const applyBullishOrderbookUpdate = (
  state: BullishOrderbookState,
  update: Pick<BullishWsData, 'bids' | 'asks' | 'sequenceNumber' | 'sequenceNumberRange'>,
): boolean => {
  const [first, last] = sequenceRange(update);
  if (state.sequence > 0 && first > state.sequence + 1) {
    throw new Error(`Bullish orderbook sequence gap: have ${state.sequence}, received ${first}`);
  }
  if (last <= state.sequence) return false;

  writeLevels(state.bids, normalizeFlatLevels(update.bids));
  writeLevels(state.asks, normalizeFlatLevels(update.asks));
  state.sequence = last;
  return true;
};

export const buildBullishSubscription = (symbol: string, topic: BullishTopic, id: string) => ({
  jsonrpc: '2.0',
  type: 'command',
  method: 'subscribe',
  params: {
    topic,
    symbol,
  },
  id,
});

const buildBullishKeepalive = (id: string) => ({
  jsonrpc: '2.0',
  type: 'command',
  method: 'keepalivePing',
  params: {},
  id,
});

const buildBullishCommandId = (offset = 0) => String(Date.now() * 1000 + offset);

export class BullishStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  sockets: ReconnectingWebSocket[] = [];
  heartbeatTimers = new Map<ReconnectingWebSocket, ReturnType<typeof setInterval>>();
  pair: string;
  displayDepth: number;
  precision = 1;
  selectedPrecision = 1;
  priceTick = '0.1';
  orderbook = createBullishOrderbookState();
  liveCandle: KLine | undefined;
  liveIntervalMs = 60_000;

  onOrderbookUpdate: (value: IOrderbook) => void;
  onTickerUpdate: (value: ITickerSocket) => void;
  onTradeUpdate: (value: TradeSocket) => void;
  klineUpdate: (value: KLine) => void;
  precisionUpdate: (value: number) => void;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    onTradeUpdate,
    klineUpdate,
    precisionUpdate,
  }: IInitArgs) {
    this.pair = buildBullishMarketSymbol(base, quote);
    this.displayDepth = orderbookDepth ?? 20;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.onTradeUpdate = onTradeUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
  }

  publishOrderbook = () => {
    const bids = [...this.orderbook.bids.entries()]
      .sort((left, right) => Number(right[0]) - Number(left[0]))
      .slice(0, this.displayDepth);
    const asks = [...this.orderbook.asks.entries()]
      .sort((left, right) => Number(left[0]) - Number(right[0]))
      .slice(0, this.displayDepth);
    this.onOrderbookUpdate({ bids, asks });
  };

  publishTicker = (ticker: BullishTicker | BullishWsData) => {
    if (
      !isFiniteNumber(ticker.last) ||
      !isFiniteNumber(ticker.open) ||
      !isFiniteNumber(ticker.high) ||
      !isFiniteNumber(ticker.low) ||
      !isFiniteNumber(ticker.baseVolume) ||
      !isFiniteNumber(ticker.quoteVolume)
    ) {
      throw new Error(`Bullish ticker ${this.pair} has an invalid shape`);
    }

    const last = asString(ticker.last!);
    this.onTickerUpdate({
      baseVolume: asString(ticker.baseVolume!),
      change: isFiniteNumber(ticker.percentage)
        ? asString(ticker.percentage!)
        : calculateChange(ticker.last!, ticker.open!),
      close: last,
      high: asString(ticker.high!),
      last,
      low: asString(ticker.low!),
      quoteVolume: asString(ticker.quoteVolume!),
    });
  };

  publishTrade = (trade: BullishTrade) => {
    const timestamp = parseTimestamp(trade.createdAtTimestamp, 'trade');
    if (
      trade.symbol.toUpperCase() !== this.pair ||
      !isFiniteNumber(trade.price) ||
      !isFiniteNumber(trade.quantity)
    ) {
      throw new Error(`Bullish public trade ${trade.tradeId || 'unknown'} is invalid`);
    }
    const side = trade.side.toLowerCase();
    if (side !== 'buy' && side !== 'sell') {
      throw new Error(`Bullish public trade ${trade.tradeId || 'unknown'} has side ${trade.side}`);
    }

    const price = asString(trade.price);
    const quantity = asString(trade.quantity);
    this.onTradeUpdate({
      price,
      quantity,
      timestamp: String(timestamp),
      side,
    });

    const bucket = Math.floor(timestamp / this.liveIntervalMs) * this.liveIntervalMs;
    if (!this.liveCandle || this.liveCandle[0] !== bucket) {
      this.liveCandle = [bucket, price, price, price, price, quantity];
    } else {
      this.liveCandle = [
        bucket,
        this.liveCandle[1],
        normalizeNumber(Math.max(Number(this.liveCandle[2]), Number(price))),
        normalizeNumber(Math.min(Number(this.liveCandle[3]), Number(price))),
        price,
        normalizeNumber(Number(this.liveCandle[5]) + Number(quantity)),
      ];
    }
    this.klineUpdate(this.liveCandle);
  };

  fetchMarket = async () => {
    try {
      const response = await bullishRestClient.get<BullishMarket>(
        `${BULLISH_REST_BASE}/v1/markets/${this.pair}`,
      );
      const market = response.data;
      if (
        market.symbol.toUpperCase() !== this.pair ||
        !market.marketEnabled ||
        !market.spotTradingEnabled ||
        !isFiniteNumber(market.tickSize) ||
        Number(market.tickSize) <= 0
      ) {
        throw new Error(`Bullish market ${this.pair} is unavailable`);
      }
      this.priceTick = asString(market.tickSize);
      this.precision = bullishPrecisionFromTick(market.tickSize);
      this.selectedPrecision = this.precision;
      this.precisionUpdate(this.precision);
    } catch (error) {
      console.log('Bullish market error:', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const response = await bullishRestClient.get<BullishOrderbookSnapshot>(
        `${BULLISH_REST_BASE}/v1/markets/${this.pair}/orderbook/hybrid`,
      );
      if (response.data.symbol && response.data.symbol.toUpperCase() !== this.pair) {
        throw new Error(`Bullish orderbook symbol ${response.data.symbol} is unexpected`);
      }
      applyBullishOrderbookSnapshot(this.orderbook, response.data);
      this.publishOrderbook();
    } catch (error) {
      console.log('Bullish orderbook error:', error);
    }
  };

  fetchTicker = async () => {
    try {
      const response = await bullishRestClient.get<BullishTicker>(
        `${BULLISH_REST_BASE}/v1/markets/${this.pair}/tick`,
      );
      if (response.data.symbol.toUpperCase() !== this.pair) {
        throw new Error(`Bullish ticker symbol ${response.data.symbol} is unexpected`);
      }
      this.publishTicker(response.data);
    } catch (error) {
      console.log('Bullish ticker error:', error);
    }
  };

  fetchKlines = async ({
    from,
    to,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }): Promise<KLine[]> => {
    const timeBucket = BULLISH_TIMEFRAMES[interval];
    if (!timeBucket) return [];

    const intervalMs = BULLISH_INTERVAL_MILLISECONDS[timeBucket];
    const boundedLimit = Math.min(BULLISH_MAX_HISTORY, Math.max(1, limit));
    const effectiveTo = to > 0 ? to : Date.now();
    const requestedFrom = from > 0 ? from : effectiveTo - boundedLimit * intervalMs;
    const effectiveFrom = Math.max(requestedFrom, effectiveTo - boundedLimit * intervalMs);
    if (effectiveFrom >= effectiveTo) return [];

    const byTimestamp = new Map<number, KLine>();
    const pageSpan = BULLISH_MAX_CANDLES_PER_REQUEST * intervalMs;

    try {
      for (let cursor = effectiveFrom; cursor < effectiveTo; cursor += pageSpan) {
        const pageEnd = Math.min(effectiveTo, cursor + pageSpan);
        const response = await bullishRestClient.get<BullishCandle[]>(
          `${BULLISH_REST_BASE}/v1/markets/${this.pair}/candle`,
          {
            params: {
              'createdAtDatetime[gte]': new Date(cursor).toISOString(),
              'createdAtDatetime[lte]': new Date(pageEnd - 1).toISOString(),
              _pageSize: BULLISH_MAX_CANDLES_PER_REQUEST,
              timeBucket,
            },
          },
        );
        normalizeBullishCandles(response.data ?? []).forEach(candle => {
          if (candle[0] >= effectiveFrom && candle[0] < effectiveTo) {
            byTimestamp.set(candle[0], candle);
          }
        });
      }
    } catch (error) {
      console.log('Bullish klines error:', error);
      return [];
    }

    return [...byTimestamp.values()].sort((left, right) => left[0] - right[0]).slice(-boundedLimit);
  };

  fetchTrades = async ({ limit }: { base: string; quote: string; limit: number }) => {
    const boundedLimit = Math.min(BULLISH_MAX_TRADES, Math.max(1, limit));
    try {
      const response = await bullishRestClient.get<BullishTrade[]>(
        `${BULLISH_REST_BASE}/v1/markets/${this.pair}/trades`,
        { params: { _pageSize: boundedLimit } },
      );
      return (response.data ?? [])
        .filter(
          trade =>
            trade.symbol?.toUpperCase() === this.pair &&
            isFiniteNumber(trade.price) &&
            isFiniteNumber(trade.quantity) &&
            isFiniteNumber(trade.createdAtTimestamp) &&
            ['BUY', 'SELL'].includes(trade.side?.toUpperCase()),
        )
        .map(trade => ({
          price: asString(trade.price),
          quantity: asString(trade.quantity),
          timestamp: asString(trade.createdAtTimestamp),
          side: trade.side.toLowerCase(),
        }));
    } catch (error) {
      console.log('Bullish trades error:', error);
      return [];
    }
  };

  handleWebSocketMessage = (message: BullishWsMessage) => {
    if (message.result?.responseCode !== undefined) {
      if (Number(message.result.responseCode) !== 200) {
        throw new Error(
          `Bullish WebSocket subscription failed: ${message.result.responseCodeName ?? message.result.responseCode} ${message.result.message ?? ''}`,
        );
      }
      return;
    }
    if (message.error?.code !== undefined || message.error?.errorCode !== undefined) {
      throw new Error(
        `Bullish WebSocket error ${String(message.error.code ?? message.error.errorCode)} ${message.error.errorCodeName ?? ''}`,
      );
    }
    if (!message.data || (message.type !== 'snapshot' && message.type !== 'update')) return;
    if (message.data.symbol?.toUpperCase() !== this.pair) return;

    if (message.dataType === 'V1TALevel2') {
      if (message.type === 'snapshot') {
        applyBullishFlatSnapshot(this.orderbook, message.data);
        this.publishOrderbook();
        return;
      }
      if (applyBullishOrderbookUpdate(this.orderbook, message.data)) this.publishOrderbook();
      return;
    }

    if (message.dataType === 'V1TATickerResponse') {
      this.publishTicker(message.data);
      return;
    }

    if (message.dataType === 'V1TAAnonymousTradeUpdate') {
      [...(message.data.trades ?? [])]
        .sort((left, right) => Number(left.createdAtTimestamp) - Number(right.createdAtTimestamp))
        .forEach(trade => this.publishTrade(trade));
    }
  };

  startHeartbeat = (socket: ReconnectingWebSocket) => {
    const existing = this.heartbeatTimers.get(socket);
    if (existing) clearInterval(existing);
    this.heartbeatTimers.set(
      socket,
      setInterval(() => {
        try {
          socket.send(JSON.stringify(buildBullishKeepalive(buildBullishCommandId())));
        } catch (error) {
          console.log('Bullish WebSocket keepalive error:', error);
        }
      }, BULLISH_HEARTBEAT_INTERVAL_MS),
    );
  };

  stopHeartbeat = (socket: ReconnectingWebSocket) => {
    const timer = this.heartbeatTimers.get(socket);
    if (timer) clearInterval(timer);
    this.heartbeatTimers.delete(socket);
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();
    this.liveIntervalMs = interval > 0 ? interval : 60_000;
    this.liveCandle = undefined;

    this.sockets = BULLISH_STREAMS.map((stream, index) => {
      const socket = new ReconnectingWebSocket(`${BULLISH_WS_BASE}${stream.path}`, [], {
        maxRetries: 10,
        minReconnectionDelay: 1000,
      });
      socket.onopen = () => {
        if (stream.topic === 'l2Orderbook') {
          this.orderbook.bids.clear();
          this.orderbook.asks.clear();
          this.orderbook.sequence = 0;
        }
        socket.send(
          JSON.stringify(
            buildBullishSubscription(this.pair, stream.topic, buildBullishCommandId(index)),
          ),
        );
        this.startHeartbeat(socket);
      };
      socket.onclose = () => this.stopHeartbeat(socket);
      socket.onmessage = event => {
        try {
          if (typeof event.data !== 'string') return;
          this.handleWebSocketMessage(JSON.parse(event.data) as BullishWsMessage);
        } catch (error) {
          console.log('Bullish websocket error:', error);
          if (stream.topic === 'l2Orderbook') void this.fetchOrderbook();
        }
      };
      return socket;
    });
    this.ws = this.sockets[0];
  };

  closeWebSocket = () => {
    this.sockets.forEach(socket => {
      this.stopHeartbeat(socket);
      socket.close();
    });
    this.sockets = [];
    this.ws = undefined;
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;
    this.publishOrderbook();
  };

  setOrderbookDepth = (depth: number) => {
    this.displayDepth = depth;
    this.publishOrderbook();
  };

  getOrderbookTicks = () => [
    {
      value: this.priceTick,
      tick: this.precision,
      id: this.precision,
    },
  ];

  fetch = async () => {
    await Promise.all([this.fetchMarket(), this.fetchOrderbook(), this.fetchTicker()]);
  };

  static init = async (args: IInitArgs) => {
    const strategy = new BullishStrategy(args);
    await strategy.fetch();
    return strategy;
  };
}
